import { useTranslation } from 'react-i18next'

import { ProgramDetailTable } from './Shared/ProgramDetailTable'
import {
  AutoFraudCheck,
  FraudCheckGroupKey,
  FraudChecksByGroup
} from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { FraudCheckType } from '@percent/admin-dashboard/api/types'
import { getFraudCheckTranslationKey } from '@percent/admin-dashboard/common/utility/getFraudCheckTranslationKey'
import { Flex, Typography } from '@percent/lemonade'

const GROUPS: { key: FraudCheckGroupKey; type: FraudCheckType }[] = [
  { key: 'basicDomainChecks', type: 'basic_domain_check' },
  { key: 'advancedDomainChecks', type: 'advanced_domain_check' },
  { key: 'applicationChecks', type: 'application_check' },
  { key: 'documentChecks', type: 'document_check' }
]

type ByGroupProps = Readonly<{ checkGroups: FraudChecksByGroup }>

// Shows where a programme differs from the default set; the phase in each row saves a table per phase.
function ByGroup({ checkGroups }: ByGroupProps) {
  const { t } = useTranslation()

  const rows = (pick: 'optInChecks' | 'disabledDefaultChecks') =>
    GROUPS.flatMap(({ key, type }) =>
      (checkGroups[key]?.[pick] ?? []).map(
        check => `${t(`program.label.fraudCheck.${type}`)}: ${t(getFraudCheckTranslationKey(check, type))}`
      )
    )

  const optIns = rows('optInChecks')
  const disabled = rows('disabledDefaultChecks')

  return (
    <>
      <Typography variant="default" $color="gray100">
        {t('program.label.fraudCheck.runsDefaults')}
      </Typography>
      <ProgramDetailTable title={t('program.label.fraudCheck.optInChecks')} items={optIns} />
      <ProgramDetailTable title={t('program.label.fraudCheck.disabledDefaultChecks')} items={disabled} />
    </>
  )
}

export function ProgramFraudCheckDetails({ checkGroups, checks }: AutoFraudCheck) {
  const { t } = useTranslation()

  const title = <Typography variant="h4">{t('typography.fraudCheck')}</Typography>

  if (checkGroups) {
    return (
      <Flex gap={16} direction="column">
        {title}
        <ByGroup checkGroups={checkGroups} />
      </Flex>
    )
  }

  // Written before the by-group model: the stored list is the complete set of what runs.
  if (!checks?.length) return null

  const hasCollections = checks.some(check => check.collection?.length)

  return (
    <Flex gap={16} direction="column">
      {title}
      {hasCollections ? (
        checks.map(({ type, collection }) => (
          <ProgramDetailTable
            key={type}
            title={t(`program.label.fraudCheck.${type}`)}
            items={collection?.map(({ key }) => t(getFraudCheckTranslationKey(key, type)))}
          />
        ))
      ) : (
        <ProgramDetailTable
          title={t('program.label.fraudCheck')}
          items={checks.map(({ type }) => t(`program.label.fraudCheck.${type}`))}
        />
      )}
    </Flex>
  )
}
